import { api } from "../api/Middleware";
import {
  AI_AGENTS_URL,
  AI_AGENTS_IMAGES_URL,
  AI_AGENTS_DRAFTS_URL,
  AI_AGENTS_GENERATE_PROMPT_URL,
  AI_AGENTS_COPY_PROMPT_URL,
  AI_AGENTS_EXPORT_URL,
  AI_AGENTS_ACTIVATE_URL,
  AI_AGENTS_VERSIONS_URL,
  AI_AGENTS_RESTORE_VERSION_URL,
  AI_AGENTS_IMAGES_BULK_UPLOAD_URL,
  AI_AGENTS_DRAFTS_CURRENT_URL,
  AI_AGENTS_DRAFTS_SAVE_URL,
  AI_AGENTS_DRAFTS_CLEAR_URL,
  AI_AGENTS_DRAFTS_CLEAR_EXPIRED_URL,
  AI_AGENTS_VERSION_DETAIL_URL,
  AI_AGENTS_CREATE_VERSION_URL,
  AI_AGENTS_EXPORT_VERSION_URL,
  AI_AGENTS_TEST_URL,
} from "../api/Urls";

// =============================
// AI AGENTS SERVICES
// =============================

// Listar todos os agentes
export const listAgents = async () => {
  return await api.get(AI_AGENTS_URL);
};

// Buscar agente específico
export const getAgent = async (id) => {
  return await api.get(AI_AGENTS_URL + `${id}/`);
};

// Criar novo agente
export const createAgent = async (data) => {
  return await api.post(AI_AGENTS_URL, data);
};

// Atualizar agente existente
export const updateAgent = async (id, data) => {
  return await api.put(AI_AGENTS_URL + `${id}/`, data);
};

// Atualizar parcialmente agente
export const patchAgent = async (id, data) => {
  return await api.patch(AI_AGENTS_URL + `${id}/`, data);
};

// Deletar agente
export const deleteAgent = async (id) => {
  return await api.delete(AI_AGENTS_URL + `${id}/`);
};

// Gerar prompt do agente
export const generateAgentPrompt = async (id) => {
  return await api.post(AI_AGENTS_GENERATE_PROMPT_URL(id));
};

// Log de cópia do prompt
export const copyAgentPrompt = async (id) => {
  return await api.post(AI_AGENTS_COPY_PROMPT_URL(id));
};

// Exportar dados do agente
export const exportAgent = async (id) => {
  return await api.get(AI_AGENTS_EXPORT_URL(id));
};

// Ativar/desativar agente
export const activateAgent = async (id) => {
  return await api.post(AI_AGENTS_ACTIVATE_URL(id));
};

// Listar versões do agente
export const getAgentVersions = async (id) => {
  return await api.get(AI_AGENTS_VERSIONS_URL(id));
};

// Restaurar versão específica do agente
export const restoreAgentVersion = async (id, versionNumber) => {
  return await api.post(AI_AGENTS_RESTORE_VERSION_URL(id), {
    version_number: versionNumber,
  });
};

// =============================
// AI AGENTS IMAGES SERVICES
// =============================

// Listar todas as imagens da biblioteca
export const listImages = async () => {
  return await api.get(AI_AGENTS_IMAGES_URL);
};

// Buscar imagem específica
export const getImage = async (id) => {
  return await api.get(AI_AGENTS_IMAGES_URL + `${id}/`);
};

// Upload de imagem única
export const uploadImage = async (formData) => {
  return await api.post(AI_AGENTS_IMAGES_URL, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Upload múltiplo de imagens
export const bulkUploadImages = async (formData) => {
  return await api.post(AI_AGENTS_IMAGES_BULK_UPLOAD_URL, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Atualizar imagem
export const updateImage = async (id, data) => {
  return await api.put(AI_AGENTS_IMAGES_URL + `${id}/`, data);
};

// Atualizar parcialmente imagem
export const patchImage = async (id, data) => {
  return await api.patch(AI_AGENTS_IMAGES_URL + `${id}/`, data);
};

// Deletar imagem
export const deleteImage = async (id) => {
  return await api.delete(AI_AGENTS_IMAGES_URL + `${id}/`);
};

// =============================
// AI AGENTS DRAFTS SERVICES
// =============================

// Listar todos os rascunhos
export const listDrafts = async () => {
  return await api.get(AI_AGENTS_DRAFTS_URL);
};

// Buscar rascunho específico
export const getDraft = async (id) => {
  return await api.get(AI_AGENTS_DRAFTS_URL + `${id}/`);
};

// Buscar rascunho atual (mais recente)
export const getCurrentDraft = async () => {
  return await api.get(AI_AGENTS_DRAFTS_CURRENT_URL);
};

// Criar novo rascunho
export const createDraft = async (data) => {
  return await api.post(AI_AGENTS_DRAFTS_URL, data);
};

// Salvar/atualizar rascunho
export const saveDraft = async (data) => {
  return await api.post(AI_AGENTS_DRAFTS_SAVE_URL, data);
};

// Atualizar rascunho existente
export const updateDraft = async (id, data) => {
  return await api.put(AI_AGENTS_DRAFTS_URL + `${id}/`, data);
};

// Atualizar parcialmente rascunho
export const patchDraft = async (id, data) => {
  return await api.patch(AI_AGENTS_DRAFTS_URL + `${id}/`, data);
};

// Deletar rascunho
export const deleteDraft = async (id) => {
  return await api.delete(AI_AGENTS_DRAFTS_URL + `${id}/`);
};

// Limpar rascunho específico
export const clearDraft = async (id) => {
  return await api.delete(AI_AGENTS_DRAFTS_CLEAR_URL(id));
};

// Limpar rascunhos expirados
export const clearExpiredDrafts = async () => {
  return await api.delete(AI_AGENTS_DRAFTS_CLEAR_EXPIRED_URL);
};

// =============================
// HELPER FUNCTIONS
// =============================

// Função auxiliar para criar FormData com arquivos de imagem
export const createImageFormData = (file, additionalData = {}) => {
  const formData = new FormData();
  formData.append("image", file);
  formData.append("name", file.name);

  // Adicionar dados adicionais se fornecidos
  Object.keys(additionalData).forEach((key) => {
    formData.append(key, additionalData[key]);
  });

  return formData;
};

// Função auxiliar para criar FormData com múltiplos arquivos
export const createBulkImageFormData = (files, additionalData = {}) => {
  const formData = new FormData();

  // Adicionar cada arquivo
  files.forEach((file) => {
    formData.append("images", file);
  });

  // Adicionar dados adicionais se fornecidos
  Object.keys(additionalData).forEach((key) => {
    formData.append(key, additionalData[key]);
  });

  return formData;
};

// Função auxiliar para preparar dados do agente para envio
export const prepareAgentData = (formData, uploadedImages = []) => {
  return {
    ...formData,
    images: uploadedImages.map((img) => img.id), // Enviar apenas os IDs das imagens
  };
};

// Função auxiliar para preparar dados do rascunho
export const prepareDraftData = (
  currentStep,
  formData,
  uploadedImages = [],
  agentId = null
) => {
  return {
    current_step: currentStep,
    form_data: formData,
    uploaded_images: uploadedImages.map((img) => img.id),
    agent_id: agentId,
  };
};

// Buscar detalhes de uma versão específica
export const getAgentVersionDetail = async (id, versionNumber) => {
  return await api.get(AI_AGENTS_VERSION_DETAIL_URL(id, versionNumber));
};

// Criar nova versão do agente
export const createAgentVersion = async (id) => {
  return await api.post(AI_AGENTS_CREATE_VERSION_URL(id));
};

// Exportar versão específica
export const exportAgentVersion = async (id, versionNumber = null) => {
  const url = versionNumber
    ? AI_AGENTS_EXPORT_VERSION_URL(id, versionNumber)
    : AI_AGENTS_EXPORT_VERSION_URL(id);
  return await api.get(url);
};

// =============================
// AI AGENTS TESTING SERVICES (NOVAS FUNÇÕES)
// =============================

// Testar agente com versão específica
export const testAgentMessage = async (payload = {}) => {

  // URL com account_id se fornecido
  const url = payload.account_id
    ? `${AI_AGENTS_TEST_URL}?account_id=${payload.account_id}`
    : AI_AGENTS_TEST_URL;

  return await api.post(url, payload);
};

// =============================
// AI AGENTS VALIDATION & STATS (NOVAS FUNÇÕES)
// =============================

// Validar se agente está pronto para testes
export const validateAgentForTesting = (agent) => {
  const errors = [];
  const warnings = [];

  // Validações obrigatórias
  if (!agent.agent_name?.trim()) {
    errors.push("Nome do agente não definido");
  }

  if (!agent.generated_prompt?.trim()) {
    errors.push("Prompt não foi gerado");
  }

  if (!agent.is_active) {
    warnings.push("Agente está inativo");
  }

  // Validações de funcionalidades
  if (agent.functions_enabled) {
    if (
      !agent.function_schemas ||
      Object.keys(agent.function_schemas).length === 0
    ) {
      warnings.push("Funções habilitadas mas nenhum schema configurado");
    }
  }

  // Validações de fluxos
  if (!agent.default_flow || Object.keys(agent.default_flow).length === 0) {
    warnings.push("Nenhum fluxo padrão configurado");
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

// Gerar estatísticas sobre o agente
export const generateAgentStats = (agent) => {
  const stats = {
    basicInfo: {
      name: agent.agent_name,
      area: agent.agent_area,
      gender: agent.agent_gender,
      version: agent.version,
      isActive: agent.is_active,
    },
    content: {
      promptLength: agent.generated_prompt?.length || 0,
      businessInfoLength: agent.business_info?.length || 0,
      prohibitedWordsCount: agent.prohibited_words
        ? agent.prohibited_words.split(",").length
        : 0,
      preferredWordsCount: agent.preferred_words
        ? agent.preferred_words.split(",").length
        : 0,
    },
    personality: {
      personaTraits: agent.agent_persona?.length || 0,
      language: agent.language?.length || 0,
      languageStyles: agent.language_style?.length || 0,
      fundamentalRules: agent.fundamental_rules?.length || 0,
    },
    flows: {
      defaultFlowSteps: agent.default_flow
        ? Object.keys(agent.default_flow).length
        : 0,
      specialFlowsCount: agent.special_flows?.length || 0,
    },
    functions: {
      enabled: agent.functions_enabled,
      schemasCount: agent.function_schemas
        ? Object.keys(agent.function_schemas).length
        : 0,
      responsesCount: agent.function_responses
        ? Object.keys(agent.function_responses).length
        : 0,
    },
    interactions: {
      enabled: agent.interactions
        ? Object.keys(agent.interactions).filter(
            (k) => agent.interactions[k]?.enabled
          ).length
        : 0,
      total: agent.interactions ? Object.keys(agent.interactions).length : 0,
    },
  };

  return stats;
};

// =============================
// AI AGENTS COMPLETE DATA LOADER (NOVA FUNÇÃO)
// =============================

// Preparar dados completos do agente para testes
export const prepareAgentTestData = async (agentId) => {
  try {
    // Carregar dados do agente
    const agentResponse = await getAgent(agentId);
    if (!agentResponse.data) {
      throw new Error("Agente não encontrado");
    }

    const agent = agentResponse.data;

    // Carregar versões
    let versions = [];
    try {
      const versionsResponse = await getAgentVersions(agentId);
      versions = versionsResponse.data.results || versionsResponse.data || [];
    } catch (error) {
      console.warn(
        "Falha ao carregar versões, usando apenas versão atual:",
        error
      );
    }

    // Preparar versão atual
    const currentVersion = {
      id: "current",
      version_number: agent.version,
      agent_data: agent,
      generated_prompt: agent.generated_prompt,
      created_at: agent.updated_at,
      is_current: true,
    };

    // Combinar versão atual com histórico
    const allVersions = [currentVersion, ...versions];

    return {
      success: true,
      data: {
        agent,
        versions: allVersions,
        currentVersion,
      },
    };
  } catch (error) {
    console.error("Erro ao preparar dados de teste:", error);
    return {
      success: false,
      error:
        error.response?.data?.detail ||
        error.message ||
        "Erro ao carregar dados do agente",
    };
  }
};

// =============================
// DOWNLOAD HELPER (NOVA FUNÇÃO)
// =============================

// Baixar versão exportada como arquivo JSON
export const downloadAgentVersionExport = async (
  agentId,
  versionNumber = null
) => {
  try {
    const response = await exportAgentVersion(agentId, versionNumber);

    if (!response.data) {
      throw new Error("Dados de export não encontrados");
    }

    const data = response.data;
    const filename = `${data.agent_info?.name || "agente"}_v${
      data.agent_info?.version || "1"
    }.json`;

    // Criar blob e download
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return {
      success: true,
      filename,
    };
  } catch (error) {
    console.error("Erro ao baixar export:", error);
    return {
      success: false,
      error:
        error.response?.data?.detail ||
        error.message ||
        "Erro ao baixar arquivo",
    };
  }
};
